import axios from 'axios';
import { LABELS } from '@/i18n/labels';
import type { Lang } from '@/stores/langStore';

/**
 * Translates a thrown forced-password-change error into a user-facing message.
 * Mirrors admin-portal `changePasswordErrorMessage` so both sites answer alike.
 *
 * POST /api/identity/change-password has ONE kind of 400: the backend rejecting the
 * request with `{ error: <message> }` — a wrong current (temporary) password
 * ("Invalid email or password") or the new password failing the house policy
 * ("Password does not meet policy requirements: ..."). Both are feedback about the
 * user's OWN input, not an internal-architecture leak, so the text is shown verbatim
 * (it is English-only on the server). Every other failure is mapped to a fixed label;
 * no raw status code and no raw `error.message` is ever surfaced.
 */
export function changePasswordErrorMessage(error: unknown, lang: Lang = 'en'): string {
  const L = (key: keyof typeof LABELS) => LABELS[key][lang];

  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return L('loginErrNetwork');
    }
    if (error.response.status === 400) {
      const body = error.response.data as { message?: unknown; error?: unknown } | undefined;
      const text =
        typeof body?.message === 'string'
          ? body.message
          : typeof body?.error === 'string'
            ? body.error
            : undefined;
      if (text) return text;
    }
    if (error.response.status >= 500) {
      return L('loginErrServer');
    }
  }
  return L('loginErrGeneric');
}
